import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {RootState} from '../../../setup/redux/Store'
import {AnnouncementModel} from '../models/AnnouncementModel'
import {NewsModel} from '../models/NewsModel'

export interface IAnnouncementState {
  announcements: AnnouncementModel[]
  currentAnnouncement: AnnouncementModel | null
  loading: boolean
  error: string | null
  dismissedIds: string[]
}

const initialState: IAnnouncementState = {
  announcements: [],
  currentAnnouncement: null,
  loading: false,
  error: null,
  dismissedIds: [],
}

// Helper function to get current language
const getCurrentLanguage = (): 'en' | 'ja' => {
  const storedLang = localStorage.getItem('kt-i18n-redux-persist')
  if (storedLang) {
    try {
      const parsed = JSON.parse(storedLang)
      return parsed.selectedLang === 'ja' ? 'ja' : 'en'
    } catch (error) {
      console.warn('Failed to parse language from localStorage')
    }
  }
  return 'en'
}

// Helper function to convert news to announcements
const convertNewsToAnnouncements = (newsList: NewsModel[], dismissedIds: string[]): AnnouncementModel[] => {
  const currentLang = getCurrentLanguage()
  
  return newsList.map((news) => ({
    id: news.id,
    message: currentLang === 'ja' ? news.title_ja : news.title_en,
    type: news.type,
    dismissible: true,
    actionUrl: news.link && news.link.trim() !== '' ? news.link : undefined,
    isDismissed: dismissedIds.includes(news.id),
  }))
}

export const announcementSlice = createSlice({
  name: 'announcement',
  initialState,
  reducers: {
    // Load dismissed IDs from localStorage
    loadDismissedIds: (state) => {
      const stored = localStorage.getItem('dismissedAnnouncements')
      if (stored) {
        try {
          state.dismissedIds = JSON.parse(stored)
        } catch (error) {
          console.warn('Failed to parse dismissed announcements from localStorage')
          state.dismissedIds = []
        }
      }
    },
    
    // Fetch news from API
    getNews: (state) => {
      state.loading = true
      state.error = null
    },
    
    // Set news data from API response
    setNews: (state, action: PayloadAction<any>) => {
      state.loading = false
      state.error = null
      
      state.announcements = convertNewsToAnnouncements(action.payload.data, state.dismissedIds)
      
      // Find the first non-dismissed announcement
      state.currentAnnouncement = state.announcements.find(
        (announcement) => !announcement.isDismissed
      ) || null
    },
    
    // Handle API error
    setError: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    
    // Dismiss an announcement
    dismissAnnouncement: (state, action: PayloadAction<{id: string; permanent: boolean}>) => {
      const {id, permanent} = action.payload
      
      // Update announcements list
      state.announcements = state.announcements.map((announcement) =>
        announcement.id === id
          ? {...announcement, isDismissed: true}
          : announcement
      )
      
      // If it's the current announcement, find the next one
      if (state.currentAnnouncement?.id === id) {
        state.currentAnnouncement = state.announcements.find(
          (announcement) => !announcement.isDismissed
        ) || null
      }
      
      // If permanent dismissal, save to localStorage
      if (permanent) {
        const newDismissedIds = [...state.dismissedIds, id]
        state.dismissedIds = newDismissedIds
        localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissedIds))
      }
    },
    
    // Show a dismissed announcement (admin functionality)
    showAnnouncement: (state, action: PayloadAction<string>) => {
      const id = action.payload
      
      // Remove from dismissed IDs
      state.dismissedIds = state.dismissedIds.filter((dismissedId) => dismissedId !== id)
      localStorage.setItem('dismissedAnnouncements', JSON.stringify(state.dismissedIds))
      
      // Update announcements list
      state.announcements = state.announcements.map((announcement) =>
        announcement.id === id
          ? {...announcement, isDismissed: false}
          : announcement
      )
      
      // If no current announcement, set this one
      if (!state.currentAnnouncement) {
        state.currentAnnouncement = state.announcements.find(
          (announcement) => announcement.id === id
        ) || null
      }
    },
    
    // Add a custom announcement (for testing or manual announcements)
    addAnnouncement: (state, action: PayloadAction<AnnouncementModel>) => {
      const newAnnouncement = action.payload
      
      // Check if announcement already exists
      const existingIndex = state.announcements.findIndex(
        (announcement) => announcement.id === newAnnouncement.id
      )
      
      if (existingIndex >= 0) {
        // Update existing
        state.announcements[existingIndex] = newAnnouncement
      } else {
        // Add new
        state.announcements.push(newAnnouncement)
      }
      
      // If no current announcement and this isn't dismissed, set it as current
      if (!state.currentAnnouncement && !newAnnouncement.isDismissed) {
        state.currentAnnouncement = newAnnouncement
      }
    },
    
    // Clear all announcements
    clearAnnouncements: (state) => {
      state.announcements = []
      state.currentAnnouncement = null
    },
  },
})

// Export actions
export const {
  loadDismissedIds,
  getNews,
  setNews,
  setError,
  dismissAnnouncement,
  showAnnouncement,
  addAnnouncement,
  clearAnnouncements,
} = announcementSlice.actions

// Export selectors
export const selectAnnouncements = (state: RootState) => state.announcement.announcements
export const selectCurrentAnnouncement = (state: RootState) => state.announcement.currentAnnouncement
export const selectAnnouncementLoading = (state: RootState) => state.announcement.loading
export const selectAnnouncementError = (state: RootState) => state.announcement.error
export const selectDismissedIds = (state: RootState) => state.announcement.dismissedIds

export default announcementSlice.reducer
